import React, { useEffect, useMemo, useState } from 'react';
import { MONTH_LABELS, periodLabelStyle, periodSelectStyle } from '../../../placeholders/periodSummaryUtils';
import { updateBudget } from '../../../../services/budgetAPI';

const yearOptionsFor = (budgetYear, extraYears = []) => {
  const years = new Set();
  const base = Number(budgetYear) || new Date().getFullYear();
  for (let year = base - 5; year <= base + 5; year += 1) years.add(year);
  extraYears.forEach((year) => {
    const value = Number(year);
    if (value) years.add(value);
  });
  return Array.from(years).sort((a, b) => a - b);
};

const horizonFromBudget = (budget) => {
  const axis = budget?.month_axis || [];
  const first = axis[0] || {};
  const last = axis[axis.length - 1] || {};
  const year = Number(budget?.year) || new Date().getFullYear();
  return {
    start_month: String(budget?.horizon_start_month || first.month || 1),
    start_year: String(budget?.horizon_start_year || first.year || year),
    end_month: String(budget?.horizon_end_month || last.month || 12),
    end_year: String(budget?.horizon_end_year || last.year || year),
  };
};

const BudgetHorizonSelector = ({ ui, budget = null, canEdit = false, onChanged }) => {
  const [form, setForm] = useState(() => horizonFromBudget(budget));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const budgetYear = Number(budget?.year) || new Date().getFullYear();
  const yearOptions = useMemo(
    () =>
      yearOptionsFor(budgetYear, [
        form.start_year,
        form.end_year,
        budget?.horizon_start_year,
        budget?.horizon_end_year,
        budget?.month_axis?.[0]?.year,
        budget?.month_axis?.[(budget?.month_axis || []).length - 1]?.year,
      ]),
    [budget, budgetYear, form.start_year, form.end_year]
  );

  useEffect(() => {
    setForm(horizonFromBudget(budget));
    setError('');
  }, [
    budget?.id,
    budget?.horizon_start_month,
    budget?.horizon_start_year,
    budget?.horizon_end_month,
    budget?.horizon_end_year,
    budget?.month_axis,
  ]);

  if (!budget?.id) return null;

  const selectStyle = {
    ...periodSelectStyle(ui),
    height: 32,
    lineHeight: '32px',
    fontSize: 12,
    minWidth: 0,
  };

  const apply = async (next) => {
    setForm(next);
    if (!canEdit || !budget?.id) return;
    setSaving(true);
    setError('');
    try {
      const payload = await updateBudget(budget.id, {
        horizon_start_month: Number(next.start_month),
        horizon_start_year: Number(next.start_year),
        horizon_end_month: Number(next.end_month),
        horizon_end_year: Number(next.end_year),
      });
      onChanged?.(payload);
    } catch (err) {
      setError(err.message || 'No se pudo guardar el periodo.');
    } finally {
      setSaving(false);
    }
  };

  const setField = (field) => (event) => apply({ ...form, [field]: event.target.value });

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
      <span style={{ ...periodLabelStyle(ui), fontSize: 10 }}>Periodo</span>
      <select value={form.start_month} onChange={setField('start_month')} disabled={!canEdit || saving} style={{ ...selectStyle, width: 88 }}>
        {MONTH_LABELS.map((label, index) => (
          <option key={`start-${label}`} value={String(index + 1)}>
            {label}
          </option>
        ))}
      </select>
      <select value={form.start_year} onChange={setField('start_year')} disabled={!canEdit || saving} style={{ ...selectStyle, width: 78 }}>
        {yearOptions.map((year) => (
          <option key={`start-year-${year}`} value={String(year)}>
            {year}
          </option>
        ))}
      </select>
      <span style={{ color: ui.muted, fontSize: 12, fontWeight: 700 }}>—</span>
      <select value={form.end_month} onChange={setField('end_month')} disabled={!canEdit || saving} style={{ ...selectStyle, width: 88 }}>
        {MONTH_LABELS.map((label, index) => (
          <option key={`end-${label}`} value={String(index + 1)}>
            {label}
          </option>
        ))}
      </select>
      <select value={form.end_year} onChange={setField('end_year')} disabled={!canEdit || saving} style={{ ...selectStyle, width: 78 }}>
        {yearOptions.map((year) => (
          <option key={`end-year-${year}`} value={String(year)}>
            {year}
          </option>
        ))}
      </select>
      {error ? <span style={{ fontSize: 11, color: ui.danger }}>{error}</span> : null}
    </div>
  );
};

export default BudgetHorizonSelector;
